import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export const useProviders = (category) => {
    const { user } = useAuth();

    const { data: providers = [], isLoading: loading, refetch } = useQuery({
        queryKey: ["providers", category],
        queryFn: async () => {
            // 1. Get verified providers from RPC
            const { data: providersData, error: providersError } = await supabase
                .rpc("get_providers_with_contact", { p_category: category || null });

            if (providersError) throw providersError;
            // Filter out global dummy/seeded providers (where added_by_user_id is null)
            let allProviders = providersData
                ? providersData.filter(p => p.added_by_user_id !== null)
                : [];

            // 2. ALSO get unverified providers added by THIS user
            if (user) {
                let query = supabase
                    .from("providers")
                    .select("*")
                    .eq("added_by_user_id", user.id)
                    .eq("is_verified", false);

                if (category) {
                    query = query.eq("category_id", category);
                }
                // No category filter: show all unverified providers added by this user

                const { data: myUnverified, error: myError } = await query;

                if (!myError && myUnverified && myUnverified.length > 0) {
                    // Merge and avoid duplicates — contact info is on the providers table directly
                    const verifiedIds = new Set(allProviders.map(p => p.id));
                    myUnverified.forEach(p => {
                        if (!verifiedIds.has(p.id)) {
                            allProviders.push(p);
                        }
                    });
                }
            }

            let providerUsage = {};
            let neighbourhoodFavorites = new Map();

            if (user) {
                const { data: serviceLogs } = await supabase
                    .from("service_logs")
                    .select("provider_id, date")
                    .not("provider_id", "is", null)
                    .eq("user_id", user.id)
                    .order("date", { ascending: false });

                if (serviceLogs) {
                    for (const log of serviceLogs) {
                        if (log.provider_id && !providerUsage[log.provider_id]) {
                            providerUsage[log.provider_id] = log.date;
                        }
                    }
                }

                // Fix: "Community Favorites" is driven purely by the Community
                // Groups the user has joined (see get_community_provider_counts,
                // supabase/migrations/20260829000003_fix_community_favorites_wiring.sql).
                // It used to also fall back to "anyone in the same suburb", which
                // meant it showed suburb-wide usage mislabeled as community-driven
                // for anyone who hadn't joined a group. p_suburb is no longer a
                // parameter of the RPC.
                const { data: communityCounts, error: communityError } = await supabase
                    .rpc("get_community_provider_counts", {
                        p_category: category || null
                    });

                if (!communityError && communityCounts) {
                    communityCounts.forEach(item => {
                        if (item.provider_id && item.usage_count > 0) {
                            neighbourhoodFavorites.set(item.provider_id, parseInt(item.usage_count));
                        }
                    });
                }
            }

            return allProviders.map(provider => ({
                ...provider,
                isMine: user && provider.added_by_user_id === user.id,
                lastUsedDate: providerUsage[provider.id] || null,
                isNeighbourhoodFavorite: neighbourhoodFavorites.has(provider.id),
                communityUsageCount: neighbourhoodFavorites.get(provider.id) || 0
            }));
        },
        enabled: !!user,
        staleTime: 1000 * 60 * 10,
    });

    return {
        providers,
        loading,
        refetch,
    };
};

export const useProviderCategories = () => {
    const { user } = useAuth();

    const { data: categories = [], isLoading: loading } = useQuery({
        queryKey: ["provider_categories", user?.id],
        queryFn: async () => {
            // First, get the master list of categories
            const { data: masterCategories } = await supabase
                .from("provider_categories")
                .select("*")
                .order("name");

            // Also get counts from providers table, excluding dummy ones (added_by_user_id is not null)
            const { data: providersData, error: providersError } = await supabase
                .from("providers")
                .select("category, category_id, is_verified, added_by_user_id")
                .not("added_by_user_id", "is", null);

            if (providersError) throw providersError;

            // Count by category_id (only count verified ones, or those added by the current user)
            const counts = {};
            for (const row of providersData || []) {
                const isVerified = row.is_verified;
                const isMine = user && row.added_by_user_id === user.id;

                if (isVerified || isMine) {
                    const catId = row.category_id || (row.category ? row.category.toLowerCase().replace(/\s+/g, "-") : null);
                    if (!catId) continue;
                    counts[catId] = (counts[catId] || 0) + 1;
                }
            }

            // Merge master list with counts
            if (masterCategories && masterCategories.length > 0) {
                return masterCategories.map(cat => ({
                    ...cat,
                    count: counts[cat.id] || 0
                }));
            } else {
                // Fallback to exactly 8 template-aligned categories
                const defaultTemplate = [
                    { id: "plumbing", name: "Plumbing" },
                    { id: "electrical", name: "Electrical" },
                    { id: "painting", name: "Painting" },
                    { id: "garden-pool", name: "Garden & Pool" },
                    { id: "security", name: "Security" },
                    { id: "roofing", name: "Roofing" },
                    { id: "hvac", name: "HVAC" },
                    { id: "gas", name: "Gas" },
                ];

                return defaultTemplate.map(cat => ({
                    ...cat,
                    count: counts[cat.id] || 0
                }));
            }
        },
        enabled: !!user,
        staleTime: 1000 * 60 * 30, // Category lists rarely change
    });

    return { categories, loading };
};
